import { useEffect, useState } from 'react';
import { useRouteError } from 'react-router';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import {
  forzarActualizacion,
  recargar,
  irAlInicio,
  hayConexion,
  estadoServiceWorker,
} from '@/lib/recovery';

/**
 * Pantalla que aparece cuando la aplicacion falla.
 *
 * Su razon de existir: la aplicacion esta configurada para PREGUNTAR antes de
 * actualizar (Req. 2.5), asi que el boton de actualizar vive dentro de ella. Si
 * el fallo ocurre antes de poder mostrarlo, no habria salida y habria que
 * borrar el icono, lo que en iOS BORRA TAMBIEN LOS DATOS.
 *
 * Aqui siempre hay una salida, y ninguna opcion toca los datos del negocio:
 * los cachés guardan los archivos del programa, los datos viven en IndexedDB.
 */
export function ErrorScreen({ error }: { error: unknown }) {
  const [conexion, setConexion] = useState(hayConexion());
  const [estadoSw, setEstadoSw] = useState('consultando…');
  const [trabajando, setTrabajando] = useState(false);
  const [avisoFallo, setAvisoFallo] = useState<string | null>(null);

  useEffect(() => {
    void estadoServiceWorker().then(setEstadoSw);

    const alCambiar = () => setConexion(hayConexion());
    window.addEventListener('online', alCambiar);
    window.addEventListener('offline', alCambiar);
    return () => {
      window.removeEventListener('online', alCambiar);
      window.removeEventListener('offline', alCambiar);
    };
  }, []);

  async function alActualizar() {
    setTrabajando(true);
    setAvisoFallo(null);
    const resultado = await forzarActualizacion();
    if (!resultado.exito) {
      setAvisoFallo(resultado.mensaje);
      setTrabajando(false);
    }
  }

  return (
    <div className="mx-auto min-h-dvh w-full max-w-lg space-y-5 px-4 pt-segura pb-segura">
      <div className="pt-6">
        <Banner tono="alerta" titulo="La aplicacion no pudo continuar">
          Algo fallo, pero <strong>tus datos siguen guardados</strong>. Ninguna de las
          opciones de abajo los borra.
        </Banner>
      </div>

      <div className="space-y-2">
        <Button
          tamano="grande"
          disabled={trabajando || !conexion}
          onClick={() => {
            void alActualizar();
          }}
        >
          {trabajando ? 'Actualizando…' : '🔄 Recargar y actualizar'}
        </Button>
        <p className="px-1 text-xs text-texto-suave">
          {conexion
            ? 'Descarta los archivos guardados del programa y baja la version mas reciente. Tus recetas, compras y ventas no se tocan.'
            : 'Necesita conexion a internet. Sin señal, descartar los archivos dejaria la aplicacion sin poder abrirse.'}
        </p>

        <Button variante="secundario" disabled={trabajando} onClick={recargar}>
          Solo recargar
        </Button>
        <Button variante="secundario" disabled={trabajando} onClick={irAlInicio}>
          Volver al inicio
        </Button>
      </div>

      {avisoFallo !== null && (
        <Banner tono="aviso" titulo="No se pudo actualizar">
          {avisoFallo}
        </Banner>
      )}

      <Banner tono="aviso" titulo="No borres el icono de la aplicacion">
        En iPhone, eliminar el icono de la pantalla de inicio{' '}
        <strong>borra tambien todos tus datos</strong>. Si nada de lo anterior funciona,
        avisame antes de desinstalarla.
      </Banner>

      <Card className="bg-superficie-suave">
        <h2 className="text-sm font-semibold text-texto">Detalle tecnico</h2>
        <dl className="mt-2 space-y-1 text-xs text-texto-suave">
          <div className="flex justify-between gap-3">
            <dt>Conexion</dt>
            <dd className="font-medium">{conexion ? 'con internet' : 'sin internet'}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt>Service Worker</dt>
            <dd className="font-medium">{estadoSw}</dd>
          </div>
        </dl>
        <pre className="mt-3 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-superficie p-2 font-mono text-[11px] text-texto-suave">
          {describirError(error)}
        </pre>
      </Card>
    </div>
  );
}

/**
 * Version para el enrutador: toma el error del contexto de rutas.
 * Se separa de ErrorScreen para que la pantalla se pueda reutilizar fuera del
 * enrutador, por ejemplo si falla la apertura de la base de datos al arrancar.
 */
export function RouteErrorScreen() {
  const error = useRouteError();
  return <ErrorScreen error={error} />;
}

function describirError(error: unknown): string {
  if (error === undefined || error === null) return 'Sin detalle disponible.';

  if (error instanceof Error) {
    const pila = error.stack ?? '';
    return `${error.name}: ${error.message}${pila === '' ? '' : `\n\n${pila}`}`;
  }

  if (typeof error === 'object') {
    const posible = error as { status?: number; statusText?: string; data?: unknown };
    if (posible.status !== undefined) {
      return `${posible.status} ${posible.statusText ?? ''}\n${String(posible.data ?? '')}`.trim();
    }
    try {
      return JSON.stringify(error, null, 2);
    } catch {
      return String(error);
    }
  }

  return String(error);
}
